import { Injectable } from '@angular/core';
import { ConfigStateService } from '@abp/ng.core';
import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

/**
 * Ponto único de envio de erros da aplicação.
 *
 * Em device, encaminha tudo para o Firebase Crashlytics. No web (o app também
 * roda como web), o plugin é no-op, então fica só o console.error — que agora
 * acontece sempre, inclusive em produção, ao contrário do resto do app, que
 * silenciava o log quando environment.production era true.
 *
 * Toda a captura (GlobalErrorHandler, interceptor de HTTP, erro de boot e o
 * watchdog do overlay) passa por captureException, então o Crashlytics fica plugado
 * só aqui. Trocar de coletor no futuro é mexer só neste arquivo.
 *
 * Ressalva conhecida: o Crashlytics symboliza stack native, não source map de
 * JS, então o erro de JS chega como non-fatal com a mensagem e o stack cru do
 * webview (no campo de log). É legível, só não tem o frame mapeado pro .ts.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  constructor(private config: ConfigStateService) {}

  /** Chamado quando o app termina de subir (ver main.ts). */
  init(): void {
    if (this.isNative) {
      void FirebaseCrashlytics.setEnabled({ enabled: true });
    }
    // A partir daqui, os erros de window capturados pelo index.html vêm direto
    // pra cá (sem race com o drain). Registra o hook antes de drenar.
    (window as any).__reportError__ = (error: unknown) =>
      this.captureException(error, { phase: 'window' });
    this.drainBootErrors();
    this.setUserFromSession();
  }

  captureException(error: unknown, context?: Record<string, unknown>): void {
    console.error('[obs] erro capturado', error, context ?? {});
    if (this.isNative) {
      void this.recordOnCrashlytics(error, context);
    }
  }

  setUser(tenantId?: string, userId?: string): void {
    // Avaliar LGPD antes de enviar identificadores ao serviço externo.
    if (!this.isNative || !userId) return;
    const id = tenantId ? `${tenantId}/${userId}` : userId;
    void FirebaseCrashlytics.setUserId({ userId: id });
  }

  private async recordOnCrashlytics(error: unknown, context?: Record<string, unknown>): Promise<void> {
    try {
      if (context) {
        for (const [key, value] of Object.entries(context)) {
          await FirebaseCrashlytics.setCustomKey({ key, value: String(value), type: 'string' });
        }
      }
      const stack = (error as { stack?: unknown })?.stack;
      if (stack) {
        await FirebaseCrashlytics.log({ message: String(stack) });
      }
      await FirebaseCrashlytics.recordException({ message: this.describe(error) });
    } catch (e) {
      console.error('[obs] falha ao reportar ao Crashlytics', e);
    }
  }

  private describe(error: unknown): string {
    if (error instanceof Error) return error.message || error.name;
    if (typeof error === 'string') return error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  private setUserFromSession(): void {
    const user = this.config.getOne('currentUser');
    if (user?.id) this.setUser(user.tenantId, user.id);
  }

  /**
   * Os erros que acontecem antes do framework subir são empilhados em
   * window.__bootErrors__ pelo script inline do index.html. Aqui a gente
   * esvazia esse buffer assim que o app está de pé.
   */
  private drainBootErrors(): void {
    const buffer = (window as any).__bootErrors__ as unknown[] | undefined;
    if (!buffer?.length) return;
    buffer.splice(0).forEach(error => this.captureException(error, { phase: 'boot' }));
  }
}
